import { z } from 'zod'
import type { IntlShape } from 'react-intl'
import { getRequiredFields } from 'util/getZodSchemaRequiredFields'
import { MeterLocation, MeterTypeKey } from '../enum'

const meterRegistyFormSchema = z.object({
	companyUUID: z.string().trim(),
	apartments: z.array(z.record(z.any())),
	apartmentUUID: z.string().trim().optional().nullable(),
	apartmentIdentifier: z.string().trim().optional().nullable(),
	meter: z.string().trim(),
	location: z.nativeEnum(MeterLocation),
	type: z.nativeEnum(MeterTypeKey),
	readingMethod: z.string().trim(),
	additionalInfo: z.string().trim().optional(),
	initialReadingValue: z.number().optional(),
	initialReadingDate: z.date().optional(),
	prefix: z.string().trim().optional(),
	suffix: z.string().trim().optional(),
	addNewMeterEvenIfExists: z.boolean(),
})

export const registryMeterApartmentsFormSchema = meterRegistyFormSchema.pick({
	companyUUID: true,
	apartments: true,
	type: true,
	readingMethod: true,
	prefix: true,
	suffix: true,
	addNewMeterEvenIfExists: true,
})

export const registryMeterFormSchema = meterRegistyFormSchema.pick({
	companyUUID: true,
	apartmentUUID: true,
	apartmentIdentifier: true,
	meter: true,
	location: true,
	type: true,
	readingMethod: true,
	additionalInfo: true,
	initialReadingValue: true,
	initialReadingDate: true,
})

export const requiredFormFields = getRequiredFields(meterRegistyFormSchema)

export const meterRegistyFormSchemaLocalized = (intl: IntlShape) => {
	const s = meterRegistyFormSchema
	return meterRegistyFormSchema.extend({
		meter: s.shape.meter.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Mittari vaaditaan.',
				description: 'Error message for required input: Meter',
			}),
		}),
		location: s.shape.location.refine(
			(val) => val !== undefined && val !== null,
			{
				message: intl.formatMessage({
					defaultMessage: 'Sijainti vaaditaan.',
					description: 'Error message for required input: Location',
				}),
			},
		),
		apartments: s.shape.apartments.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Huoneistot vaaditaan.',
				description: 'Error message for required input: Apartments',
			}),
		}),
		type: s.shape.type.refine((val) => val !== undefined && val !== null, {
			message: intl.formatMessage({
				defaultMessage: 'Tyyppi vaaditaan.',
				description: 'Error message for required input: Type',
			}),
		}),
		readingMethod: s.shape.readingMethod.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Sisäänlukutapa vaaditaan.',
				description: 'Error message for required input: Reading method',
			}),
		}),
	})
}

const meterRegistyBaseSchema = (intl: IntlShape) =>
	meterRegistyFormSchemaLocalized(intl).pick({
		companyUUID: true,
		apartmentUUID: true,
		apartmentIdentifier: true,
		meter: true,
		location: true,
		type: true,
		readingMethod: true,
		additionalInfo: true,
		initialReadingValue: true,
		initialReadingDate: true,
	})

export const meterRegistySchema = (intl: IntlShape) =>
	meterRegistyBaseSchema(intl).superRefine((data, ctx) => {
		if (data.location === MeterLocation.APARTMENT) {
			if (!data.apartmentUUID) {
				ctx.addIssue({
					code: 'custom',
					message: intl.formatMessage({
						defaultMessage: 'Huoneisto vaaditaan.',
						description: 'Error message for required input: ApartmentUUID',
					}),
					path: ['apartmentUUID'],
				})
			}
			if (!data.apartmentIdentifier) {
				ctx.addIssue({
					code: 'custom',
					message: intl.formatMessage({
						defaultMessage: 'Huoneistotunniste vaaditaan.',
						description:
							'Error message for required input: ApartmentIdentifier',
					}),
					path: ['apartmentIdentifier'],
				})
			}
		}
	})

export const meterRegistyApartmentsSchema = (intl: IntlShape) =>
	meterRegistyFormSchemaLocalized(intl).pick({
		companyUUID: true,
		apartments: true,
		type: true,
		readingMethod: true,
		prefix: true,
		suffix: true,
		addNewMeterEvenIfExists: true,
	})
